export namespace _CloudArmor {
  type Match = {
    versionedExpr?: string;
    config?: { srcIpRanges: string[] };
    expr?: { expression: string };
  };

  export type Rule = {
    action: string;
    description?: string;
    preview?: boolean;
    priority: number;
    match: Match;
  };

  export type BuildDefaultRulesOptions = {
    customRules?: Rule[];
  };

  // 既定ルール群に割り当てる priority の想定レンジ。
  export const DEFAULT_RULE_PRIORITY_MIN = 1000;
  export const DEFAULT_RULE_PRIORITY_MAX = 5000;
  // Cloud Armor の「最後の catch-all ルール」で使う終端 priority。
  // 利用側で allow all を明示して evaluation を閉じるために公開している。
  export const CATCH_ALL_PRIORITY = 2147483647;

  // OWASP CRS のうち誤検知(FP)が多いシグネチャを除外する ID 一覧。
  // evaluatePreconfiguredExpr の第2引数に渡し、実運用でノイズが大きいものを抑制する。
  // see: https://github.com/plaidev/karte-io-terraform/blob/master/terraform/gcp/production-karte-io-systems/compute_security_policy.tf
  const wafExcludeIds = {
    sqli: [
      'owasp-crs-v030001-id942110-sqli',
      'owasp-crs-v030001-id942120-sqli',
      'owasp-crs-v030001-id942150-sqli',
      'owasp-crs-v030001-id942180-sqli',
      'owasp-crs-v030001-id942190-sqli',
      'owasp-crs-v030001-id942200-sqli',
      'owasp-crs-v030001-id942210-sqli',
      'owasp-crs-v030001-id942251-sqli',
      'owasp-crs-v030001-id942260-sqli',
      'owasp-crs-v030001-id942300-sqli',
      'owasp-crs-v030001-id942310-sqli',
      'owasp-crs-v030001-id942330-sqli',
      'owasp-crs-v030001-id942340-sqli',
      'owasp-crs-v030001-id942380-sqli',
      'owasp-crs-v030001-id942390-sqli',
      'owasp-crs-v030001-id942400-sqli',
      'owasp-crs-v030001-id942410-sqli',
      'owasp-crs-v030001-id942420-sqli',
      'owasp-crs-v030001-id942421-sqli',
      'owasp-crs-v030001-id942430-sqli',
      'owasp-crs-v030001-id942431-sqli',
      'owasp-crs-v030001-id942432-sqli',
      'owasp-crs-v030001-id942440-sqli',
      'owasp-crs-v030001-id942450-sqli',
      'owasp-crs-v030001-id942460-sqli',
      'owasp-crs-v030001-id942220-sqli',
    ],
    xss: [
      'owasp-crs-v030001-id941150-xss',
      'owasp-crs-v030001-id941320-xss',
      'owasp-crs-v030001-id941330-xss',
      'owasp-crs-v030001-id941340-xss',
      'owasp-crs-v030001-id941180-xss',
      'owasp-crs-v030001-id941160-xss',
    ],
    rce: [
      'owasp-crs-v030001-id932100-rce',
      'owasp-crs-v030001-id932105-rce',
      'owasp-crs-v030001-id932110-rce',
      'owasp-crs-v030001-id932115-rce',
      'owasp-crs-v030001-id932120-rce',
      'owasp-crs-v030001-id932130-rce',
      'owasp-crs-v030001-id932140-rce',
    ],
    lfi: ['owasp-crs-v030001-id930100-lfi', 'owasp-crs-v030001-id930110-lfi', 'owasp-crs-v030001-id930120-lfi'],
    rfi: ['owasp-crs-v030001-id931130-rfi'],
    protocolattack: [
      'owasp-crs-v030001-id921120-protocolattack',
      'owasp-crs-v030001-id921130-protocolattack',
      'owasp-crs-v030001-id921150-protocolattack',
      'owasp-crs-v030001-id921151-protocolattack',
      'owasp-crs-v030001-id921160-protocolattack',
      'owasp-crs-v030001-id921170-protocolattack',
    ],
    scannerdetection: ['owasp-crs-v030001-id913101-scannerdetection', 'owasp-crs-v030001-id913102-scannerdetection'],
  } as const;

  const toQuotedList = (ids: readonly string[]) => ids.map((id) => `'${id}'`).join(', ');

  // 既定WAFルール群。priority は将来の差し込み余地を残すため 10 刻みで採番。
  // 多くは preview=true で「遮断せずログ観測」から開始し、誤検知を見て段階的に enforce する前提。
  const defaultExprRules = [
    {
      expression: "request.path.lower().endsWith('.js.map')",
      description: 'Information Gathering(Source Map): Deny access to source map',
      priority: 1010,
      preview: false,
    },
    {
      expression: `evaluatePreconfiguredExpr('sqli-stable', [${toQuotedList(wafExcludeIds.sqli)}])`,
      description: 'SQLi: only sensitivity level 1 that exclude rules with many FP',
      priority: 2010,
      preview: true,
    },
    {
      expression: `evaluatePreconfiguredExpr('xss-stable', [${toQuotedList(wafExcludeIds.xss)}])`,
      description: 'XSS: only sensitivity level 1 that exclude rules with many FP',
      priority: 2020,
      preview: true,
    },
    {
      expression: `evaluatePreconfiguredExpr('rce-canary', [${toQuotedList(wafExcludeIds.rce)}])`,
      description: 'RCE: only sensitivity level 1 that exclude rules with many FP',
      priority: 2030,
      preview: true,
    },
    {
      expression: `evaluatePreconfiguredExpr('lfi-canary', [${toQuotedList(wafExcludeIds.lfi)}])`,
      description: 'LFI: only sensitivity level 1 that exclude rules with many FP',
      priority: 2040,
      preview: true,
    },
    {
      expression: `evaluatePreconfiguredExpr('rfi-canary', [${toQuotedList(wafExcludeIds.rfi)}])`,
      description: 'RFI: only sensitivity level 1 that exclude rules with many FP',
      priority: 2050,
      preview: true,
    },
    {
      expression: `evaluatePreconfiguredExpr('protocolattack-stable', [${toQuotedList(wafExcludeIds.protocolattack)}])`,
      description: 'Protocol Attack: only sensitivity level 1 that exclude rules with many FP',
      priority: 2060,
      preview: true,
    },
    {
      expression: `evaluatePreconfiguredExpr('scannerdetection-stable', [${toQuotedList(wafExcludeIds.scannerdetection)}])`,
      description: 'Scanner Detection: only sensitivity level 1 that exclude rules with many FP',
      priority: 2070,
      preview: true,
    },
    {
      expression: "evaluatePreconfiguredExpr('cve-canary')",
      description: 'CVE',
      priority: 2080,
      preview: true,
    },
  ] as const;

  export const buildDefaultRules = (options?: BuildDefaultRulesOptions): Rule[] => {
    const defaultRules = defaultExprRules.map((rule) => ({
      action: 'deny(403)',
      description: rule.description,
      match: {
        expr: {
          expression: rule.expression,
        },
      },
      preview: rule.preview,
      priority: rule.priority,
    }));
    const customRules = options?.customRules ?? [];
    const mergedRules = [...defaultRules, ...customRules];

    return mergedRules;
  };
}
